import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';

interface EmailTagsInputProps {
  emails: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
}

const EmailTagsInput: React.FC<EmailTagsInputProps> = ({ 
  emails, 
  onChange, 
  placeholder = "Add moderator email..." 
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Simple email validation regex
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const addEmail = (email: string) => {
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) return;
    
    if (isValidEmail(trimmedEmail)) {
      if (!emails.includes(trimmedEmail)) {
        onChange([...emails, trimmedEmail]);
      }
      setInputValue('');
      setIsInvalid(false);
    } else {
      setIsInvalid(true);
    }
  };

  const removeEmail = (index: number) => {
    const newEmails = [...emails];
    newEmails.splice(index, 1);
    onChange(newEmails);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ' || e.key === ';') {
      e.preventDefault();
      addEmail(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      // Remove the last tag when backspace is pressed and input is empty
      removeEmail(emails.length - 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    // Split pasted text by common separators
    const pastedEmails = pastedText.split(/[,;\s]+/);
    
    pastedEmails.forEach(email => {
      if (email.trim()) {
        addEmail(email);
      }
    });
  };

  // Focus the container to activate the input
  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    // Add click event listener to the container
    if (containerRef.current) {
      containerRef.current.addEventListener('click', focusInput);
    }
    
    return () => {
      // Clean up event listener
      if (containerRef.current) {
        containerRef.current.removeEventListener('click', focusInput);
      }
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`email-tags-input ${isInvalid ? 'invalid' : ''}`}
      style={{
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '5px',
        display: 'flex',
        flexWrap: 'wrap',
        minHeight: '38px',
        alignItems: 'center',
        cursor: 'text',
        backgroundColor: '#fff',
        borderColor: isInvalid ? '#ff6b6b' : '#ccc'
      }}
    >
      {emails.map((email, index) => (
        <div 
          key={index}
          style={{
            backgroundColor: '#e0f7e0',
            borderRadius: '3px',
            padding: '4px 8px',
            margin: '2px',
            display: 'flex',
            alignItems: 'center',
            fontSize: '14px'
          }}
        >
          <span style={{ marginRight: '5px' }}>{email}</span>
          <button
            type="button"
            onClick={() => removeEmail(index)}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              fontSize: '12px',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          if (isInvalid) setIsInvalid(false);
        }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={() => {
          if (inputValue.trim()) {
            addEmail(inputValue);
          }
        }}
        placeholder={emails.length === 0 ? placeholder : ''}
        style={{
          flex: '1',
          border: 'none',
          outline: 'none',
          minWidth: '120px',
          padding: '4px',
          fontSize: '14px'
        }}
      />
      {isInvalid && (
        <div style={{ 
          width: '100%', 
          color: '#ff6b6b', 
          fontSize: '12px', 
          marginTop: '4px',
          paddingLeft: '4px'
        }}>
          Please enter a valid email address
        </div>
      )}
    </div>
  );
};

export default EmailTagsInput;